import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { PersonalAccountMenuService } from '@zaoblako/web/core/services/lk/personal-account-menu.service';
import type { ExternalUserAccessControlInterface } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-access-control.dto';
import type { ExternalUserFindInterface } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-find.dto';
import type { ProfileLoginFindInterface } from '@zaoblako/shared/modules/security/dtos/profile/profile-login-find.dto';
import type { ProfileLoginFormInterface } from '@zaoblako/shared/modules/security/dtos/profile/profile-login-form.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import type { RoleEnum } from '@zaoblako/shared/modules/security/enums/role.enum';
import { SessionModeEnum } from '@zaoblako/shared/modules/security/enums/session-mode.enum';
import { ProfileService } from '@zaoblako/web/core/services/security/profile.service';
import { TelemetryService } from '@zaoblako/web/core/services/telemetry.service';
import { isNil } from 'lodash';
import { Observable, of, take } from 'rxjs';
import { tap } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private loggedInUser: ProfileLoginFindInterface;

  public get currentProfile() {
    return this.loggedInUser;
  }

  public get currentUser(): UserFindInterface {
    switch (this.loggedInUser?.sessionMode) {
    case SessionModeEnum.TENANT:
      return this.loggedInUser?.user;
    case SessionModeEnum.PERSONAL_ACCOUNT:
      return this.loggedInUser?.externalUser as any;
    case SessionModeEnum.PROFILE:
      return null;
    }
  }

  public get isParkingBlocked(): boolean {
    if (this.loggedInUser?.sessionMode !== SessionModeEnum.PERSONAL_ACCOUNT) {
      return false;
    }

    return this.loggedInUser?.externalUser?.client?.isParkingBlocked;
  }

  public get currentTypeTenant(): 'MAIN' | 'LK' {
    if (typeof location?.hostname === 'string' && location.hostname.startsWith('lk-')) {
      return 'LK';
    }
    return 'MAIN';
  }

  public get getToken(): string {
    return localStorage.getItem('JWT_TOKEN');
  }

  public constructor(
    private readonly router: Router,
    private readonly profileService: ProfileService,
    private readonly cookieService: CookieService,
    private readonly telemetryService: TelemetryService,
    private readonly injector: Injector
  ) {
    const currentUser = localStorage.getItem('JWT_USER');
    if (currentUser) {
      this.setUser(JSON.parse(currentUser));
    }
  }

  public setToken(token: string) {
    localStorage.setItem('JWT_TOKEN', token);
  }

  public login(login: ProfileLoginFormInterface): Observable<ProfileLoginFindInterface> {
    return this.profileService.login(login).pipe(
      tap((user) => {
        this.setUser(user);
      })
    );
  }

  public logout(url?: string) {
    this.telemetryService.disconnect();
    localStorage.removeItem('JWT_TOKEN');
    localStorage.removeItem('JWT_USER');

    this.cookieService.delete('JWT_TOKEN', '/', location.hostname.slice(location.hostname.indexOf('.')));

    this.loggedInUser = undefined;
    this.injector.get(GlobalService).resetSessionState();
    this.injector.get(PersonalAccountMenuService).clearCache();
    this.router.navigate(['/login'], { queryParams: { url } }).catch(console.error);
  }

  public updateLoggedUser(user: Partial<ProfileLoginFindInterface>) {
    this.setUser({ ...this.loggedInUser, ...user });
  }

  public isAuthenticated(): Observable<ProfileLoginFindInterface> {
    if (!this.getToken) {
      return of(null);
    }
    return this.profileService.check().pipe(take(1));
  }

  public hasRole(roles: RoleEnum | RoleEnum[]): boolean {
    const currentUserRoles = (this.currentProfile?.user?.roles || []).map((role) => role.type);
    if (!currentUserRoles) {
      return false;
    }

    return Array.isArray(roles)
      ? roles.some((role) => currentUserRoles.includes(role))
      : currentUserRoles.includes(roles);
  }

  public setUser(user: ProfileLoginFindInterface) {
    const preservedSessionMode = user?.sessionMode ?? this.loggedInUser?.sessionMode;
    this.loggedInUser = user;
    if (!isNil(preservedSessionMode) && this.loggedInUser) {
      this.loggedInUser.sessionMode = preservedSessionMode;
    }

    localStorage.setItem('JWT_TOKEN', user.token);
    localStorage.setItem('JWT_USER', JSON.stringify(user));

    const domain = `.${
      location.hostname
        .split('.')
        .slice(-2)
        .join('.')
    }`;
    this.cookieService.set('JWT_TOKEN', user.token, { secure: true, sameSite: 'None', path: '/', domain: domain });

    this.telemetryService.connect(user.token);
  }

  public get currentAccessControl(): ExternalUserAccessControlInterface {
    if (this.currentTypeTenant !== 'LK') {
      return null;
    }
    return (this.currentUser as any as ExternalUserFindInterface)?.accessControl;
  }

  public hasAccess(rightKey: keyof ExternalUserAccessControlInterface): boolean {
    const accessControl = this.currentAccessControl;
    return !!accessControl?.[rightKey];
  }

  public get isLkAdmin(): boolean {
    return this.hasAccess('admin');
  }
}
